import { useState } from 'react';
import { motion } from 'framer-motion';
import ContactModal from './ContactModal';

const tickets = [
  {
    id: 'for-teams',
    label: 'For teams',
    tag: '01',
    title: "Your customer ops run on vibes. Let's fix that.",
    body: "Journey mapping, stakeholder interviews, process design, and AI-assisted systems that make your customer operations visible — so decisions stop being guesses. I've done this inside SaaS companies for a decade. Now I do it for yours.",
    cta: 'Start a conversation →',
    modalTitle: "Let's fix your customer ops.",
  },
  {
    id: 'for-job-seekers',
    label: 'For job seekers',
    tag: '02',
    title: 'Job Search Ops Setup: your own pipeline in a week.',
    body: 'The matcher, the daily brief, the dashboard — built for your profile and your search. Fixed scope, fixed price, and you keep the whole system. Because drowning in alerts is not a strategy, and guilt is not a metric.',
    cta: 'Get your pipeline →',
    modalTitle: 'Get your own Job Search Ops pipeline.',
  },
];

export default function OpenTickets() {
  const [openId, setOpenId] = useState(null);

  return (
    <section id="tickets" className="relative py-28 md:py-40 bg-gradient-to-b from-lilac/40 to-paper" data-testid="tickets-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="flex items-baseline justify-between mb-14">
          <div className="mono text-xs uppercase tracking-[0.3em] text-orchid">(06) — Open tickets</div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-ink/40 hidden md:block">Two doors. Pick yours.</div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="serif text-[12vw] md:text-[8vw] leading-[0.9] text-ink mb-16 md:mb-24"
        >
          Two doors. <span className="italic text-grape">Pick yours.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
          {tickets.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8, delay: i * 0.08 }}
              className="relative bg-paper border border-ink/15 p-8 md:p-12 hover:border-orchid transition-colors"
              data-testid={`ticket-${t.id}`}
            >
              <div className="flex items-baseline justify-between mb-8">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid">Ticket · {t.tag}</div>
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-ink/40">{t.label}</div>
              </div>
              <h3 className="serif text-3xl md:text-4xl text-ink leading-[1.05] mb-8">{t.title}</h3>
              <p className="mono text-[14px] md:text-[15px] text-ink/80 leading-relaxed mb-10">{t.body}</p>
              <button
                type="button"
                onClick={() => setOpenId(t.id)}
                data-testid={`ticket-cta-${t.id}`}
                className="inline-block bg-void text-paper px-6 py-3.5 mono uppercase tracking-[0.22em] text-xs hover:bg-orchid transition-colors duration-300"
              >
                {t.cta}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-16 mono text-sm text-ink/60 max-w-2xl">
          Not sure which door?{' '}
          <button
            type="button"
            onClick={() => setOpenId('general')}
            data-testid="tickets-write-me-link"
            className="text-ink underline decoration-orchid decoration-2 underline-offset-4 hover:text-orchid transition-colors bg-transparent"
          >
            Write to me
          </button>{' '}
          anyway. Worst case, we have a nice conversation and you leave with one useful idea. I&rsquo;ve had worse Tuesdays.
        </p>
      </div>

      {tickets.map((t) => (
        <ContactModal
          key={t.id}
          open={openId === t.id}
          onClose={() => setOpenId(null)}
          project={t.label}
          title={t.modalTitle}
        />
      ))}
      <ContactModal
        open={openId === 'general'}
        onClose={() => setOpenId(null)}
        project="general"
        title="Drop me a note."
      />
    </section>
  );
}
